import { BackButton } from "@/components/custom/BackButton"
import { PageTitle } from "@/components/custom/PageTitle"
import { ButtonVariant } from "@/constants/button/button"
import { TagValue } from "./components/TagValue"
import { WorkflowAppRunStatus } from "./components/WorkflowAppRunStatus"
import { WorkflowRunStatusType } from '@/constants/workflowHistory/workflowHistory'
import { AlertDialogButton } from '@/components/custom/AlertDialogButton/AlertDialogButton'
import { AlertDialogButtons, AlertDialogDescription, AlertDialogTitle } from '@/constants/alert-dialog/alert-dialog'
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { AppDispatch, RootState } from '@/redux'
import { useDispatch, useSelector } from 'react-redux'
import { getExecutionDetails, reRunByExecutionId, reRunStep } from '@/services/WorkflowExecutionService'
import { StepError, WorkflowExecutionInDetail, WorkflowTaskExecution } from '@/types/workflowHistory'
import { formatISOStringToDate } from '@/helpers/date'
import { InputOutput } from "./components/InputOutput"
import { Button } from "@/components/ui/button"
import { RotateCw } from "lucide-react"
import { ConfirmationModal } from "@/components/custom/AlertDialogButton/ConfirmationModal"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"

export const WorkflowRunDetails = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { runId } = useParams()
  const { token } = useSelector((state: RootState) => state.user)
  const { runs } = useSelector((state: RootState) => state.workflowRunHistory)
  const [executionDetails, setExecutionDetails] = useState<WorkflowExecutionInDetail | undefined>(undefined)
  const [openReRunModal, setOpenReRunModal] = useState(false)
  const [runFollowingSteps, setRunFollowingSteps] = useState(false)
  const [selectedStep, setSelectedStep] = useState<WorkflowTaskExecution | undefined>(undefined)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)

  const lastStepNumber = executionDetails?.workflowTaskExecutions?.reduce(
    (max, step) => Math.max(max, step.step ?? 0),
    0
  )
  const canIncludeFollowingSteps =
    Boolean(selectedStep) &&
    typeof selectedStep?.step === 'number' &&
    typeof lastStepNumber === 'number' &&
    selectedStep.step < lastStepNumber

  const fetchExecutionDetails = async (shouldLoad = true) => {
    if (!token || !runId) return
    const resp = await dispatch(getExecutionDetails(token, runId, shouldLoad))
    if (resp.data?.workflowExecution) {
      setExecutionDetails(resp.data.workflowExecution)
    } else if (resp.err) {
      console.error(resp.err)
      navigate('/workflows')
    }
  }

  useEffect(() => {
    if (!token || !runId) return

    // Initial fetch with loading
    fetchExecutionDetails(true)

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, runId])

  // Poll every 30s only if still processing or ready
  useEffect(() => {
    if (!executionDetails) return

    const isActive =
      executionDetails.status === WorkflowRunStatusType.PROCESSING ||
      executionDetails.status === WorkflowRunStatusType.READY

    if (isActive && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        fetchExecutionDetails(false) // no loading screen while polling
      }, 30000)
    } else if (!isActive && pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }

    return () => {
      if (pollingRef.current && !isActive) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionDetails?.status])

  const getStepStatus = (error: StepError) => {
    if (!error || Object.keys(error).length === 0) {
      return WorkflowRunStatusType.SUCCESS
    }
    if (typeof error === "string") {
      try {
        const parsedError = JSON.parse(error);
        if (Object.keys(parsedError).length > 0) {
          return WorkflowRunStatusType.ERROR
        }
      } catch (e) {
        return WorkflowRunStatusType.ERROR
      }
    }
    return WorkflowRunStatusType.ERROR
  }

  const getErrorMessageIfAny = (error: StepError) => {
    if (!error) return ""

    // if it's already a string
    if (typeof error === "string") {
      try {
        const parsed = JSON.parse(error)
        if (parsed.message) return parsed.message
        if (parsed.error) return parsed.error
        return JSON.stringify(parsed, null, 2)
      } catch {
        return error
      }
    }

    // if it's an object
    if (typeof error === "object") {
      if ("message" in error && typeof error.message === "string") return error.message
      if ("apiCall" in error && "functionName" in error) {
        return `Error in ${error.functionName}: ${error.apiCall}`
      }
      if ("name" in error && typeof error.name === "string") return error.name
      // As fallback, stringify the whole thing
      return JSON.stringify(error, null, 2)
    }

    return ""
  }

  const onReRunButtonClick = async () => {
    if (!token || !runId) return
    const resp = await dispatch(reRunByExecutionId(token, runId))
    if (resp.data) {
      navigate(`/workflow-history/${runs?.workflowId ?? ''}`)
    }
  }

  const reRunStepById = async (stepId: string, shouldRunAllFollowingSteps: boolean) => {
    if (!token) return
    const resp = await dispatch(reRunStep(token, stepId, shouldRunAllFollowingSteps))
    if (resp.data) {
      navigate(`/workflow-history/${runs?.workflowId ?? ''}`)
    }
  }

  return (
    <div className="protectedPageLayout">
      <BackButton buttonLink="/workflow-history" buttonText="Back to Workflow History" />
      <PageTitle title="Workflow Run Details" />
      {executionDetails?.status === 'failed' && (
        <div className="absolute top-10 right-10">
          <AlertDialogButton
            actionButtonText={AlertDialogButtons.RE_RUN_ALL_STEPS}
            dialogTitle={AlertDialogTitle.RE_RUN_ALL_STEPS}
            dialogSubtitle={AlertDialogDescription.RE_RUN_ALL_STEPS}
            onClick={() => onReRunButtonClick()}
            variant={ButtonVariant.DEFAULT}
            isTableActionButton={false}
            isDisabled={false}
          />
        </div>
      )}
      <div className="flex items-center text-primary">
        <h4 className="font-semibold text-lg leading-4 mr-2">Run ID:</h4>
        <p>{executionDetails?.id}</p>
      </div>
      <div className="flex items-center text-primary">
        <h4 className="font-semibold text-lg leading-4 mr-2">Run Date:</h4>
        <p>{formatISOStringToDate(executionDetails?.dateCreated)}</p>
      </div>
      {executionDetails?.workflowTaskExecutions.sort((a, b) => a.step - b.step).map((taskExecution, i) => {
        const status =
          executionDetails.status === WorkflowRunStatusType.PROCESSING &&
            taskExecution?.error &&
            Object.keys(taskExecution.error).length === 0
            ? executionDetails.status
            : getStepStatus(taskExecution?.error as StepError)
        const errorMessage = getErrorMessageIfAny(taskExecution?.error as StepError)
        const appLogoURL = taskExecution.app.logo
        const hasFilters = taskExecution?.workflowTaskExecutionFilters
        const hasOptions = taskExecution?.workflowTaskExecutionOptions
        const credentials = taskExecution.workflowTaskExecutionCredentials.map(c => c.credential.nickname).join(', ')
        return (
          <div className="bg-foreground p-8 w-full flex flex-col items-start mt-8" key={taskExecution.id + i}>
            <div className="w-full flex flex-row items-center justify-between">
              <WorkflowAppRunStatus
                status={status}
                appLogoUrl={appLogoURL}
                message={errorMessage}
              />
              <Button
                variant={ButtonVariant.DEFAULT}
                className="font-semibold hover:opacity-90"
                onClick={() => {
                  setOpenReRunModal(true)
                  setSelectedStep(taskExecution)
                  setRunFollowingSteps(false)
                }}
              >
                <RotateCw size={16} className="mr-2" />
                Re-run Step
              </Button>
            </div>
            <h2 className="font-semibold text-xl leading-8 text-primary mb-1">
              Step {i + 1}. {taskExecution.app.displayName || 'Transformation'}
            </h2>
            <p className="text-primary mb-6">
              {formatISOStringToDate(executionDetails?.dateCreated)}
            </p>
            <TagValue
              className="mb-4"
              title='Credentials'
              subtitle={credentials}
            />
            {hasFilters?.length ? (
              <TagValue
                className='mb-4'
                title='Filters'
                subtitle={hasFilters.map(f => `${f.filter}: ${f.filterValue}`).join(', ')}
              />
            ) : null}
            {hasOptions?.length ? (
              <TagValue
                className='mb-4'
                title='Options'
                subtitle={hasOptions.map(o => `${o.option}: ${o.optionValue}`).join(', ')}
              />
            ) : null}
            {taskExecution.input ? (
              <InputOutput data={taskExecution.input} title="Input" />
            ) : null}
            {taskExecution.output ? (
              <InputOutput data={taskExecution.output} title="Output" />
            ) : null}
          </div>
        )
      })}
      {/* Re-Run Confirmation */}
      <ConfirmationModal
        open={openReRunModal}
        onOpenChange={setOpenReRunModal}
        actionButtonText={AlertDialogButtons.RE_RUN_SINGLE_STEP}
        dialogTitle={`Re-run "Step ${selectedStep ? selectedStep.step + 1 : ''} - ${selectedStep?.app.displayName || 'Transformation'}" ?`}
        dialogSubtitle={canIncludeFollowingSteps ? AlertDialogDescription.RE_RUN_SINGLE_STEP : AlertDialogDescription.RE_RUN_LAST_STEP}
        variant={ButtonVariant.DEFAULT}
        onConfirm={() => {
          if (selectedStep) reRunStepById(selectedStep.id, canIncludeFollowingSteps ? runFollowingSteps : false)
        }}
      >
        {canIncludeFollowingSteps ? (
          <div className="mt-4 flex items-center gap-2">
            <Checkbox
              id="rerunFollowingSteps"
              checked={runFollowingSteps}
              onCheckedChange={(checked) => setRunFollowingSteps(Boolean(checked))}
            />
            <Label
              htmlFor="rerunFollowingSteps"
              className="font-semibold text-primary !text-sm cursor-pointer"
            >
              Include all steps after this one
            </Label>
          </div>
        ) : null}
      </ConfirmationModal>
    </div>
  )
}
